import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../../utils/api';

// Static model lists per provider
const OPENAI_MODELS = ['gpt-5-mini', 'gpt-5-nano', 'gpt-5', 'gpt-5.2'];
const GOOGLE_MODELS = ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-3-preview', 'gemini-3-flash-preview'];

// Default models per provider
const DEFAULT_MODELS = {
  openai: 'gpt-5-mini',
  ollama: 'llama3',
  google: 'gemini-2.5-flash',
};

// Model field name per provider (used when storing in node config)
const MODEL_FIELD_NAMES = {
  openai: 'model',
  ollama: 'ollama_model',
  google: 'google_model',
};

/**
 * AIModelSelect -- unified provider + model selector for AI features.
 *
 * Props:
 *   provider        - current provider value ('openai' | 'ollama' | 'google')
 *   model           - current model value (string)
 *   onProviderChange - (newProvider) => void
 *   onModelChange    - (newModel) => void
 *   disabled         - disable both selects
 *   size             - MUI size prop ('small' | 'medium'), defaults to 'small'
 *   providerLabel    - label for the provider dropdown
 *   modelLabel       - label for the model dropdown
 *   showHelperText   - whether to show helper text under selects
 *   direction        - layout direction ('row' | 'column'), defaults to 'row'
 */
const AIModelSelect = ({
  provider = 'openai',
  model = '',
  onProviderChange,
  onModelChange,
  disabled = false,
  size = 'small',
  providerLabel = 'AI Provider',
  modelLabel = 'Model',
  showHelperText = false,
  direction = 'row',
}) => {
  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaLoading, setOllamaLoading] = useState(false);
  const [ollamaError, setOllamaError] = useState(false);

  // Fetch Ollama models when provider is 'ollama'
  useEffect(() => {
    if (provider !== 'ollama') return;

    let cancelled = false;
    setOllamaLoading(true);
    setOllamaError(false);

    api
      .get('/ollama/models')
      .then((res) => {
        if (!cancelled) {
          const models = res.data.models || [];
          setOllamaModels(models);
          // If the current model is not in the fetched list, pick the first one
          if (models.length > 0) {
            const modelNames = models.map((m) => m.name);
            if (!modelNames.includes(model)) {
              onModelChange(models[0].name);
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOllamaModels([]);
          setOllamaError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setOllamaLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [provider]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    onProviderChange(newProvider);
    // Set default model for the new provider
    onModelChange(DEFAULT_MODELS[newProvider] || '');
  };

  const renderModelOptions = () => {
    switch (provider) {
      case 'openai':
        return OPENAI_MODELS.map((m) => (
          <MenuItem key={m} value={m}>
            {m}
          </MenuItem>
        ));

      case 'google':
        return GOOGLE_MODELS.map((m) => (
          <MenuItem key={m} value={m}>
            {m}
          </MenuItem>
        ));

      case 'ollama':
        if (ollamaLoading) {
          return (
            <MenuItem value={model || DEFAULT_MODELS.ollama} disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Loading models...
            </MenuItem>
          );
        }
        if (ollamaModels.length > 0) {
          return ollamaModels.map((m) => (
            <MenuItem key={m.name} value={m.name}>
              {m.name} {m.parameter_size ? `(${m.parameter_size})` : ''}
            </MenuItem>
          ));
        }
        // Fallback when no models found
        return (
          <MenuItem value={model || DEFAULT_MODELS.ollama}>
            {model || DEFAULT_MODELS.ollama}
          </MenuItem>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: direction,
          gap: 2,
        }}
      >
        <FormControl fullWidth size={size} disabled={disabled}>
          <InputLabel>{providerLabel}</InputLabel>
          <Select
            value={provider}
            onChange={handleProviderChange}
            label={providerLabel}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="ollama">Ollama (Local)</MenuItem>
            <MenuItem value="google">Google</MenuItem>
          </Select>
          {showHelperText && (
            <FormHelperText>Select the AI provider to use</FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth size={size} disabled={disabled || ollamaLoading}>
          <InputLabel>{modelLabel}</InputLabel>
          <Select
            value={model || DEFAULT_MODELS[provider] || ''}
            onChange={(e) => onModelChange(e.target.value)}
            label={modelLabel}
          >
            {renderModelOptions()}
          </Select>
          {showHelperText && provider === 'ollama' && ollamaModels.length === 0 && !ollamaLoading && (
            <FormHelperText error>
              No Ollama models found. Check Ollama connection in Settings.
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {provider === 'ollama' && ollamaError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Could not connect to Ollama. Check the Ollama Base URL in Settings.
        </Alert>
      )}
    </Box>
  );
};

export { OPENAI_MODELS, GOOGLE_MODELS, DEFAULT_MODELS, MODEL_FIELD_NAMES };
export default AIModelSelect;
